import { useMemo } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Download04Icon } from "@hugeicons/core-free-icons";
import { Button } from "@/components/ui/button";
import {
  ANALYTICS_COMPANY_LIMIT,
  CompanyAnalyticsCard,
  FetchAllProgressDialog,
  PeriodSelector,
  TrendDialog,
  useAnalyticsCompanies,
  useFetchAllSummaries,
  useOrderAnalyticsStore,
  validatePeriod,
} from "@/features/orderAnalytics";

export default function OrderAnalyticsPage() {
  const period = useOrderAnalyticsStore((s) => s.period);
  const { data, isLoading, error } = useAnalyticsCompanies();
  const companies = useMemo(() => data?.dispatchCompanies ?? [], [data]);
  const { progress, isRunning, start, abort } = useFetchAllSummaries(companies);

  const isPeriodValid = validatePeriod(period) === null;
  const totalCount = data?.totalCount ?? 0;

  return (
    <div className="flex h-full flex-col bg-slate-50/50 p-4">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">배차 업체별 오더 분석</h1>
          <p className="mt-0.5 text-xs text-gray-600">
            카드를 클릭하면 설정한 기간의 요약 지표를 조회합니다. 지표는 전일까지 집계된 통계 기준입니다
          </p>
        </div>
        <div className="flex items-start gap-2">
          <PeriodSelector disabled={isRunning} />
          <Button
            size="sm"
            disabled={isRunning || !isPeriodValid || companies.length === 0}
            onClick={start}
            className="h-7 bg-blue-600 px-3 text-xs font-bold hover:bg-blue-700"
          >
            <HugeiconsIcon icon={Download04Icon} size={14} className="mr-1.5" />
            모두 가져오기
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center text-xs text-gray-400">
            업체 목록을 불러오는 중...
          </div>
        ) : error ? (
          <div className="flex h-32 items-center justify-center text-xs text-red-600">
            업체 목록을 불러오지 못했습니다: {error.message}
          </div>
        ) : companies.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-xs text-gray-400">
            등록된 배차 업체가 없습니다
          </div>
        ) : (
          <>
            <p className="mb-2 text-xs text-gray-500">
              배차 업체 {companies.length}곳
              {totalCount > ANALYTICS_COMPANY_LIMIT &&
                ` (전체 ${totalCount}곳 중 최근 등록 ${ANALYTICS_COMPANY_LIMIT}곳만 표시)`}
            </p>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(340px,1fr))] gap-3">
              {companies.map((company) => (
                <CompanyAnalyticsCard key={company.companyGuid} company={company} />
              ))}
            </div>
          </>
        )}
      </div>

      <FetchAllProgressDialog progress={progress} onAbort={abort} />
      <TrendDialog />
    </div>
  );
}
